import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PageTransition } from "@/layout/PageTransition";
import { Header } from "@/components/Header";
import { PageHeaderProvider } from "@/layout/PageHeaderProvider";
import { NewProjectProvider } from "@/pages/projekte/NewProjectContext";
import { OrderWizardProvider } from "@/pages/bestellungen/OrderWizardContext";
import { AnfrageWizardProvider } from "@/pages/anfragen/AnfrageWizardProvider";
import { DocumentCreateProvider } from "@/pages/dokumente/DocumentCreateProvider";
import { DocumentDialogsMount } from "@/pages/dokumente/DocumentDialogsMount";
import { DocumentEditorProvider } from "@/pages/dokumente/DocumentEditorProvider";
import { ProjectOverviewProvider, ProjectOverviewDialogMount } from "@/projectOverview/ProjectOverviewContext";
import { SOrdnerOverviewProvider } from "@/sordnerOverview/SOrdnerOverviewContext";
import { TimesheetFlowProvider } from "@/features/timesheetFlow/TimesheetFlowProvider";
import { AskCommissionAfterCreateHost } from "@/pages/projekte/AskCommissionAfterCreateHost";
import { GlobalUploadProvider } from "@/features/globalUpload/GlobalUploadProvider";
import { maybeRedirectReactToLegacyOnLoad } from "@/lib/designPreview";
import { startSessionRefreshLoop } from "@/lib/sessionRefresh";
import { AppUpdateBanner } from "@/features/appUpdate/AppUpdateBanner";
import { HeroOutageBanner } from "@/components/HeroOutageBanner";
import { SafetyModeBanner } from "@/components/SafetyModeBanner";
import { BirthdayProvider } from "@/features/birthday/BirthdayProvider";
import { VacationProvider, useVacation } from "@/features/vacation/VacationProvider";
import { VacationWishBanner } from "@/components/VacationWishBanner";
import { DecoPreferenceProvider } from "@/features/deco/DecoPreferenceProvider";
import { DesignPreferenceProvider, useDesignPreference } from "@/features/design/DesignPreferenceProvider";
import { LayoutPreferenceProvider, useLayoutPreference } from "@/features/layout/LayoutPreferenceProvider";
import { useElectronNavigateBridge } from "@/lib/useElectronNavigateBridge";
import { InAppWindowFocusProvider } from "@/lib/inAppWindowFocus";
import { ModalStackProvider } from "@/lib/modalStack";
import { TourProvider } from "@/features/tours/TourProvider";
import { TourOverlay } from "@/features/tours/TourOverlay";
import { IncomingCallHost } from "@/features/incomingCall/IncomingCallHost";

function ElectronNavigateBridge() {
  useElectronNavigateBridge();
  return null;
}

function DesignPreviewRouteGuard() {
  const { loading, newDesign } = useDesignPreference();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!newDesign) {
      maybeRedirectReactToLegacyOnLoad(location.pathname, location.search, location.hash);
    }
  }, [loading, newDesign, location.hash, location.pathname, location.search]);

  return null;
}

export function Layout() {
  useEffect(() => {
    // Gleitende Session-Verlängerung starten, sobald der authentifizierte
    // Bereich gerendert wird (idempotent – läuft pro Tab nur einmal).
    startSessionRefreshLoop();
  }, []);

  return (
    <TourProvider>
    <DesignPreferenceProvider>
    <LayoutPreferenceProvider>
    <DecoPreferenceProvider>
    <VacationProvider>
    <BirthdayProvider>
    <PageHeaderProvider>
      <ModalStackProvider>
      <OrderWizardProvider>
      <InAppWindowFocusProvider>
      <ProjectOverviewProvider>
      {/* GlobalUpload innerhalb der Projektübersicht, damit Drop das offene Projekt vorauswählt. */}
      <GlobalUploadProvider>
      <DocumentEditorProvider>
      {/* NewProjectProvider muss DocumentCreateProvider umschließen: der Create-Dialog
          rendert NewProjectButton als Sibling von children, nicht darunter. */}
      <NewProjectProvider>
      <DocumentCreateProvider>
      <TimesheetFlowProvider>
        <SOrdnerOverviewProvider>
          <AnfrageWizardProvider>
            <div className="flex min-h-screen min-w-0 flex-col overflow-x-clip bg-grouped text-label">
              <ElectronNavigateBridge />
              <DesignPreviewRouteGuard />
              <SafetyModeBanner />
              <HeroOutageBanner />
              <Header />
              <LayoutVacationWish />
              <AppUpdateBanner />
              <LayoutMain />
              <footer className="eh-site-footer sticky bottom-0 z-40 border-t border-separator bg-grouped/95 py-3 text-center text-xs text-label-secondary backdrop-blur-md supports-[backdrop-filter]:bg-grouped/80">
                Ernsthaus App · Interne Arbeitsplattform
              </footer>
            </div>
            <ProjectOverviewDialogMount />
            <IncomingCallHost />
            <DocumentDialogsMount />
            <AskCommissionAfterCreateHost />
            <TourOverlay />
          </AnfrageWizardProvider>
        </SOrdnerOverviewProvider>
      </TimesheetFlowProvider>
      </DocumentCreateProvider>
      </NewProjectProvider>
      </DocumentEditorProvider>
      </GlobalUploadProvider>
      </ProjectOverviewProvider>
      </InAppWindowFocusProvider>
      </OrderWizardProvider>
      </ModalStackProvider>
    </PageHeaderProvider>
    </BirthdayProvider>
    </VacationProvider>
    </DecoPreferenceProvider>
    </LayoutPreferenceProvider>
    </DesignPreferenceProvider>
    </TourProvider>
  );
}

function LayoutMain() {
  const { fullWidth } = useLayoutPreference();

  return (
    <main
      className={
        fullWidth
          ? "eh-layout-main eh-layout-main--full w-full min-w-0 max-w-none flex-1 py-6"
          : "eh-layout-main eh-layout-main--contained mx-auto w-full min-w-0 max-w-7xl flex-1 py-6"
      }
    >
      <div className="eh-layout-main__inset min-w-0 px-4 sm:px-6">
        <PageTransition />
      </div>
    </main>
  );
}

function LayoutVacationWish() {
  const { showWishBanner, wishMessage, dismissWish } = useVacation();
  if (!showWishBanner) return null;
  return <VacationWishBanner message={wishMessage} onDismiss={dismissWish} />;
}
